import { ChangeDetectorRef, Component, inject, output, signal } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { XClose } from '../../../infrastructure/shared/icons/x-close/x-close';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { Skeleton } from 'primeng/skeleton';
import { Chip } from 'primeng/chip';
import { Badge } from 'primeng/badge';
import { debounceTime, firstValueFrom, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';

import {
  AppointmentForAdminTableResponse,
  AppointmentsClient,
  AppointmentStatus,
  GetAppointmentsForAdminQuery,
  IAppointmentFilter,
  IPaginationResponseOfAppointmentForAdminTableResponse, SetUserPackageStatusCommand
} from '../../../infrastructure/shared/model/fss.api';
import { MIN_LOADING_TIME_MS } from '../../../infrastructure/shared/constants';

import { CalendarCheck01 } from '../../../infrastructure/shared/icons/calendar-check-01/calendar-check-01';
import { ClockRewind } from '../../../infrastructure/shared/icons/clock-rewind/clock-rewind';
import { Arrow } from '../../../infrastructure/shared/icons/arrow/arrow';
import { Search } from '../../../infrastructure/shared/icons/search/search';
import { SlashCircle01 } from '../../../infrastructure/shared/icons/slash-circle-01/slash-circle-01';

import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { TextInput } from '../../../infrastructure/shared/ui/text-input/text-input';
import { FormControlPipe } from '../../../infrastructure/shared/pipes/form-control.pipe';
import { ClientTypeDropdown } from '../../../infrastructure/shared/components/client-type-dropdown/client-type-dropdown';
import { convertDateToHhMm, getDateFormatedSr, getMonthShortSr } from '../../../infrastructure/shared/services/date.service';
import { AlertTriangle } from '../../../infrastructure/shared/icons/alert-triangle/alert-triangle';
import { Attachment02 } from '../../../infrastructure/shared/icons/attachment-02/attachment-02';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { AppointmentDetailsDrawer } from './appointment-details-drawer/appointment-details-drawer';
import { AppointmentRescheduleDialog } from './appointment-reschedule-dialog/appointment-reschedule-dialog';
import { Edit03 } from '../../../infrastructure/shared/icons/edit-03/edit-03';

@Component({
  standalone: true,
  selector: 'app-admin-appointments-table',
  imports: [
    Tab,
    TabList,
    Tabs,
    TableModule,
    Skeleton,
    Chip,
    CalendarCheck01,
    ClockRewind,
    Arrow,
    Search,
    SlashCircle01,
    ButtonComponent,
    TextInput,
    FormControlPipe,
    ClientTypeDropdown,
    AlertTriangle,
    Attachment02,
    Dialog,
    XClose,
    AppointmentDetailsDrawer,
    AppointmentRescheduleDialog,
    Edit03,
    Badge,
  ],
  templateUrl: './admin-appointments-table.html',
  styleUrl: './admin-appointments-table.scss',
})
export class AdminAppointmentsTable {
  private _subs = new Subscription();
  private _client = inject(AppointmentsClient);
  private _cdr = inject(ChangeDetectorRef);
  private _loader = inject(LoaderService);
  private _alert = inject(AlertService);

  /** Details drawer */
  showDetailsDrawer = signal(false);
  selectedDetailsId = signal<string | undefined>(undefined);

  openDetails(id: string) {
    this.selectedDetailsId.set(id);
    this.showDetailsDrawer.set(true);
  }

  onDetailsCancel(_id: string) {
    this.onAction.emit();
    this.load();
  }

/** Reschedule dialog */
  showRescheduleDialog = signal(false);
  selectedRescheduleId = signal<string | undefined>(undefined);

  openRescheduleDialog(id: string) {
    this.selectedRescheduleId.set(id);
    this.showRescheduleDialog.set(true);
  }

  onRescheduled() {
    this.onAction.emit();
    this.load();
  }

  onAction = output<void>();

  appointments: AppointmentForAdminTableResponse[] = [];
  first = 0;
  isLoading = true;
  filter: IAppointmentFilter | undefined;
  hasActiveFilters = false;
  activeTab = 0;
  resetCounter = 0;
  searchFormCtrl: FormControl = new FormControl(undefined);

  /** Count of upcoming appointments shown as a badge on the "Predstojeće" tab */
  upcomingCount: number | undefined;

  paginatedData: IPaginationResponseOfAppointmentForAdminTableResponse = {
    currentPage: 1,
    pageSize: 4,
    totalCount: 0,
  } as IPaginationResponseOfAppointmentForAdminTableResponse;

  constructor() {
    this.filter = {
      isUpcoming: true,
      keyword: undefined,
      isCompany: undefined,
    } as IAppointmentFilter;

    this._subs.add(
      this.searchFormCtrl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
        if (value === '') {
          value = undefined;
        }
        this.filter!.keyword = value;
        this.load();
      }),
    );
  }

  loadAppointmentsLazy(event: any) {
    this.paginatedData.pageSize = event.rows;
    this.paginatedData.currentPage = Math.floor(event.first / event.rows) + 1;
    this.load();
  }

  next() {
    this.paginatedData!.currentPage!++;
    this.load();
  }

  prev() {
    this.paginatedData!.currentPage!--;
    this.load();
  }

  private load() {
    this.isLoading = true;
    this.appointments = [];
    this._checkHasActiveFilters();
    this._cdr.detectChanges();
    const start = Date.now();

    this._loadAppointments().then((res) => {
      const elapsed = Date.now() - start;
      const delay = Math.max(0, MIN_LOADING_TIME_MS - elapsed);

      setTimeout(() => {
        this.appointments = res.result.data ?? [];
        this.paginatedData = res.result;
        this.upcomingCount = res.result.additionalData;
        this.first = (this.paginatedData.currentPage! - 1) * this.paginatedData.pageSize!;
        this.isLoading = false;
        this._cdr.markForCheck();
      }, delay);
    });
  }

  private async _loadAppointments() {
    return await firstValueFrom(
      this._client.getAppointmentsForAdmin({
        filter: this.filter,
        pageNumber: this.paginatedData.currentPage,
        pageSize: this.paginatedData.pageSize,
      } as GetAppointmentsForAdminQuery),
    );
  }

  onTabChange(value: any) {
    this.activeTab = value;
    this.filter!.isUpcoming = this.activeTab === 0;
    this.load();
  }

  onClientTypeChange(value: boolean | undefined) {
    this.filter!.isCompany = value;
    this.load();
  }

  resetFilters() {
    this.filter!.isCompany = undefined;
    this.searchFormCtrl.patchValue(undefined);
    this.resetCounter++;
  }

  private _checkHasActiveFilters() {
    this.hasActiveFilters =
      this.filter?.keyword !== undefined || this.filter?.isCompany !== undefined;
  }

  getStatusLabel(appt: AppointmentForAdminTableResponse): string {
    const isPast = !!appt.to && appt.to.getTime() < Date.now();
    if (
      isPast &&
      appt.status?.value !== AppointmentStatus.CANCELED &&
      appt.status?.value !== AppointmentStatus.NO_SHOW
    ) {
      return 'Završen';
    }
    return appt.status?.description ?? '';
  }

  getStatusChipClass(status: AppointmentStatus | undefined): string {
    if (status === AppointmentStatus.CANCELED) {
      return 'red sm';
    }
    if (status === AppointmentStatus.NO_SHOW) {
      return 'warn sm';
    }
    return this.activeTab === 1 ? 'gray sm' : 'info sm';
  }

  formatTimeRange(from?: Date, to?: Date): string {
    if (!from || !to) return '';
    return `${convertDateToHhMm(from)} - ${convertDateToHhMm(to)}`;
  }

  getMonthLabel(date?: Date): string {
    if (!date) return '';
    return getMonthShortSr(date);
  }

  getDayLabel(date?: Date): string {
    if (!date) return '';
    return String(date.getDate()).padStart(2, '0');
  }

  getPrimaryDateLabel(date?: Date): string {
    if (!date) return '';
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (sameDay(date, today)) return 'Danas';
    if (sameDay(date, tomorrow)) return 'Sutra';
    // The badge already shows month + day; this line adds the year.
    return getDateFormatedSr(date);
  }

  /** Cancel appointment dialog */
  showCancelDialog = signal(false);
  selectedForCancel = signal<{ id: string; name: string } | undefined>(undefined);

  openCancelDialog(id: string, name: string) {
    this.selectedForCancel.set({ id, name });
    this.showCancelDialog.set(true);
  }

  closeCancelDialog() {
    this.showCancelDialog.set(false);
    this.selectedForCancel.set(undefined);
  }

  confirmCancel() {
    this._loader.setLoadingText = 'Otkazivanje...';
    this._loader.setShowLoader = true;
    this._cancel(this.selectedForCancel()!.id).then((res) => {
      this.closeCancelDialog();
      this.onAction.emit();
      this.load();
      this._alert.addSuccessMsg(res.result);
    });
  }

  private async _cancel(id: string) {
    return await firstValueFrom(this._client.cancelAppointment(id));
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly AppointmentStatus = AppointmentStatus;
}
